import { NgClass, NgStyle } from '@angular/common';
import { Component, Directive, ElementRef, EventEmitter, Input, OnDestroy, Optional, Output, ViewChild } from '@angular/core';
import { FormsModule, NgModel } from '@angular/forms';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatError, MatFormField, MatHint, MatInput, MatLabel, MatPrefix, MatSuffix } from '@angular/material/input';
import { MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { AutocompleteOffDirective } from 'directives/autocomplete-off.directive';
import { HtmlEditorDirective } from 'directives/html-editor.directive';
import { InputStateDirective } from 'directives/input-state.directive';
import { ValidationDirective } from 'directives/validation.directive';
import { InputType } from 'enums/InputType';
import { ValidationType } from 'enums/validationType';
import { ApprovalStatus, InputTypeApprovalModel } from 'models/generated/Models.InputTypeData.InputTypeApproval';
import { AutosizeModule } from 'ngx-autosize';
import { CurrencyPipe } from 'pipes/currency.pipe';
import { TranslatePipe } from 'pipes/translate.pipe';
import { FormValidationService } from 'services/form-validation.service';
import { InputContainerService } from 'services/input-container.service';
import { NumberOptions, NumberService } from 'services/number.service';

type ValueType = string | number | boolean | Date | object;

@Component({
	selector: 'custom-input',
	templateUrl: './custom-input.component.html',
	imports: [NgClass, MatSlideToggle, ValidationDirective, FormsModule, MatCheckbox, MatFormField, MatLabel, InputStateDirective, MatInput, AutocompleteOffDirective, NgStyle, MatPrefix, MatSuffix, MatError, MatHint, HtmlEditorDirective, AutosizeModule, TranslatePipe, MatRadioGroup, MatRadioButton]
})
export class CustomInputComponent implements CustomInput, OnDestroy {

	@ViewChild('container') containerElement?: ElementRef<HTMLElement>;
	@ViewChild('input') inputElement?: ElementRef<HTMLInputElement>;
	@ViewChild(MatInput) matInput?: MatInput;
	@ViewChild(NgModel) ngModel?: NgModel;


	@Output() valueChange = new EventEmitter();
	@Output() blur = new EventEmitter();

	public checkboxValue = false;
	public isCheckbox = false;
	public radioButtons: { value: ValueType, label: string; }[] = [];
	public multiLine = false;

	private _value?: ValueType;

	@Input() label = '';
	@Input() labelPosition: 'before' | 'after' = 'after';
	@Input() vertical = false;
	@Input() hint = '';
	@Input() prefix?: string;
	@Input() suffix?: string;
	@Input() disabled = false;
	@Input() allowMultiLine = false;

	@Input()
	set value(val: ValueType | undefined) {

		if (this.inputType == InputType.Text && typeof val == 'string' && val.indexOf('\n') >= 0) this.multiLine = true;

		if (this.isCheckbox) {
			if (this._value == val) return;
			this._value = val;
			this.checkboxValue = val!.toString().toLowerCase() == 'true';
		} else {

			let formatValue = !this.matInput?.focused;
			if (this.inputType == InputType.Approval && val != undefined && val !== '') {
				this.approval = val as InputTypeApprovalModel;
			}
			if (this.noAnswer && val !== '') {
				this.noAnswer = false;
				this.displayValue = val?.toString();
				formatValue = true;
			}

			this._value = val;
			if (formatValue) this.formatValue();
		}
	}
	get value() { return this._value == null ? undefined : this._value; }

	private _inputType: InputType = InputType.Text;
	@Input()
	set inputType(type: InputType | string) {
		if (typeof type === 'string') {
			const match = Object.keys(InputType).findIndex(key => key.toLowerCase() === type);
			if (match >= 0) type = Object.values(InputType)[match] as InputType;
			else type = InputType.Text;
		}
		this._inputType = type;
		if (this.inputType === InputType.Switch || this.inputType === InputType.Checkbox) {
			this.isCheckbox = true;
			if (this.hasValue())
				this.checkboxValue = this.value!.toString().toLowerCase() == 'true';
		}
		if (type == InputType.Approval && this.hasValue()) {
			this.approval = JSON.parse(this.value!.toString());
		}

		if (type == InputType.Currency) this.allowedPattern = '[0-9,\.]*';
		else if (type == InputType.Number) this.allowedPattern = '[0-9]*';
		else if (type == InputType.Percentage) this.allowedPattern = '[0-9\%]*';
		else this.allowedPattern = '.*';

		this.formatValue();
	}
	get inputType() {
		return this._inputType;
	}

	private _noAnswer = false;
	@Input()
	set noAnswer(val: boolean) {
		this._noAnswer = val;
		this.formatValue();
	}
	get noAnswer() { return this._noAnswer; }

	@Input() placeholder = '';
	@Input() textAlign = 'left';
	@Input() class = '';
	@Input() validation?: ValidationType;
	@Input() required = false;
	@Input() ignoreFormValidation = false;
	@Input() customValidState? : boolean;
	@Input() customValidationMessage? : string;
	@Input() maxlength?: string;
	
	allowedPattern = '.*';
	valid = true;
	touched = false;
	displayValue? = '';
	specialBehaviorClass = '';
	inputTypeEnum = InputType;
	approvalStatusEnum = ApprovalStatus;
	validationTypeEnum = ValidationType;
	validationMessage = '';
	approval?: InputTypeApprovalModel;
	set approvalStatus(status: ApprovalStatus | undefined) {
		if (this.approval === undefined)
			this.approval = (this.value !== undefined && this.value !== '' ? JSON.parse(this.value.toString()) : {}) as InputTypeApprovalModel;
		if (this.approval.approvalStatus == status)
			return;
		this.approval.approvalStatus = status as ApprovalStatus;
		this.valueChanged();

		this.validationService?.fieldStateChanged(this, true, false);
	}

	markAsPristine() { this.validationService?.fieldStateChanged(this, true, true); }

	get approvalStatus(): ApprovalStatus | undefined {
		return this.approval !== undefined ? this.approval.approvalStatus : undefined;
	}

	constructor(
		private translate: TranslatePipe,
		private currency: CurrencyPipe,
		private numberService: NumberService,
		@Optional() private readonly inputContainer: InputContainerService,
		@Optional() private readonly validationService: FormValidationService) {

		this.inputContainer?.add(this);
	}

	ngOnDestroy() {
		this.inputContainer?.remove(this);
		this.validationService?.removeField(this);
	}

	hasFocus(): boolean {
		if (this.containerElement) {
			for (let child of this.containerElement.nativeElement.children) {
				if (child == document.activeElement || child.classList.contains('redactor-focus')) {
					this.touched = true;
					return true;
				}
			}
		}
		return false;
	}
	setFocus(selectAll = false) {
		if (selectAll) this.inputElement?.nativeElement?.select();
		else this.inputElement?.nativeElement?.focus();
	}
	setBlur() {
		this.inputElement?.nativeElement?.blur();
	}
	onBlur() {
		this.formatValue();
		this.blur.emit();
	}

	onResized() {
		this.inputContainer?.resized.emit();
	}

	private hasValue() {
		return this.value != undefined && this.value !== '';
	}

	formatValue() {
		this.specialBehaviorClass = '';
		if (this.noAnswer) {
			this.specialBehaviorClass = 'no-answer';
			this.displayValue = this.translate.transform('general.no-answer');
			return;
		}
		if (this.inputType == InputType.Currency && this.hasValue())
			this.displayValue = this.currency.transform(this.value as number);
		else if (this.inputType == InputType.Approval && this.approval !== undefined) {
			this.displayValue = this.approval.comment;
		} else if (this.inputType == InputType.Percentage && this.hasValue()) {
			this.displayValue = this.value !== undefined ? this.value.toString() + '%' : undefined;
		}
		else
			this.displayValue = this.value !== undefined ? this.value.toString() : undefined;

		this.validateField();
	}

	validateField() {
		this.validationMessage = '';
		if (!this.valid) {
			if (!this.displayValue) this.validationMessage = 'required';
			else if (this.customValidationMessage) {
				this.validationMessage = this.customValidationMessage;
			}
			else if (this.validation !== undefined && this.validation !== null) {
				this.validationMessage = this.validationTypeEnum[this.validation];
			}
		}
	}

	valueChanged() {
		if (this.isCheckbox) {
			this.displayValue = this.checkboxValue.toString();
			this.emitValueChange(this.checkboxValue);
		}
		else if (this.inputType == InputType.Radio) {
			this.emitValueChange(this.value);
		}
		else {
			this.emitValueChange(this.displayValue);
		}
	}

	emitValueChange(value?: ValueType) {
		if (this.inputType == InputType.Approval && this.approval !== undefined) {
			this.approval.comment = value as string;
			this.valueChange.emit(this.approval);
			return;
		}
		if (this.inputType == InputType.Currency && this.hasValue()) {
			value = this.numberService.getNumber(value!.toString(), new NumberOptions(false));
		}
		this.valueChange.emit(value);
	}

	editValue() {
		this.validationMessage = '';
		this.specialBehaviorClass = '';
		if (this.hasFocus() && this.noAnswer) {
			this.displayValue = '';
			return;
		}

		if (this.inputType == InputType.Currency && this.hasValue()) {
			this.displayValue = this.numberService.formatNumber(Number(this.value), 2, new NumberOptions(false));
		} else if (this.inputType == InputType.Approval && this.approval !== undefined) {
			this.displayValue = this.approval.comment;
		} else if (!this.noAnswer)
			this.displayValue = this.value !== undefined ? this.value.toString() : undefined;
	}

	validateKeyInput(event: KeyboardEvent) {
		const numbers = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'];
		const currency = numbers.concat(',', '.');
		const systemKeys = [
			'Escape', 'Backspace', 'Delete', 'Insert', 'Home', 'End', 'PageUp', 'PageDown', 'Tab', 'Shift',
			'ArrowUp', 'ArrowRight', 'ArrowDown', 'ArrowLeft', 'F5'
		];

		if (event.key == 'Enter' && event.shiftKey && this.setMultiLine()) {
			event.stopImmediatePropagation();
			event.stopPropagation();
			return false;
		}

		if (this.inputType === InputType.Text ||
			this.inputType === InputType.Textarea ||
			this.inputType == InputType.Approval ||
			this.inputType === InputType.Editor) {
			return true;
		}
		if (this.inputType === InputType.Number || this.inputType === InputType.Percentage) {
			if (numbers.indexOf(event.key) !== -1) {
				this.validationMessage = '';
				return true;
			} else if (!event.ctrlKey && !event.altKey && systemKeys.indexOf(event.key) < 0) {
				this.validationMessage = 'number';
			}
		}
		if (this.inputType === InputType.Currency) {
			if (currency.indexOf(event.key) !== -1) {
				this.validationMessage = '';
				return true;
			} else if (!event.ctrlKey && !event.altKey && systemKeys.indexOf(event.key) < 0) {
				this.validationMessage = 'currency';
			}
		}
		if (event.ctrlKey || event.altKey) {
			return true;
		}
		if (systemKeys.indexOf(event.key) !== -1) {
			return true;
		}

		return false;
	}

	private setMultiLine() {
		
		// Only act if Text input, multiLine is allowed, and not currently multiLine
		if (this.inputType !== InputType.Text || !this.allowMultiLine || this.multiLine) return false;

		// Add line break at current caret position
		const caretPosition = this.inputElement?.nativeElement.selectionEnd ?? this.inputElement?.nativeElement.selectionStart ?? this.inputElement?.nativeElement.value.length ?? 0;
		if (this.displayValue) this.displayValue = this.displayValue.substring(0, caretPosition) + '\n' + this.displayValue.substring(caretPosition);
		this.valueChanged();

		// Change to show textarea
		this.multiLine = true;

		// Once textarea element is visible, set focus and place caret after the inserted line break
		setTimeout(() => {
			if (!this.inputElement) return;
			this.inputElement.nativeElement.focus();
			this.inputElement.nativeElement.selectionStart = this.inputElement.nativeElement.selectionEnd = caretPosition + 1;
		});
		
		return true;
	}

	validatePasteInput(event: any) {
		if (this.inputType == InputType.Number || this.inputType == InputType.Currency || this.inputType === InputType.Percentage)
			if (isNaN(this.numberService.getNumber(event.clipboardData.getData('Text'), new NumberOptions(false)))) return false;

		return true;
	}

	showTextarea() {
		return (this.multiLine && this.allowMultiLine) ||
			this.inputType == InputType.Textarea ||
			this.inputType == InputType.Editor ||
			(this.inputType == InputType.Approval && this.approval !== undefined && this.approval.approvalStatus == ApprovalStatus.Rejected);
	}
}

export interface CustomInput {
	setFocus: (autoFocus?: boolean) => void;
}

@Directive({ selector: 'custom-input radio-button' })
export class CustomRadioButtonDirective {

	@Input({ required: true }) public value: ValueType = '';
	@Input({ required: true }) public label = '';
	constructor(customInput: CustomInputComponent) {
		customInput.radioButtons.push(this);
	}
}
